/**
 * captureSchema — Discriminated form schemas by entry type.
 *
 * Replaces the loose `Record<string, string>` form state with a discriminated
 * union that makes invalid states unrepresentable. Each ChildEntryType has its
 * own schema with only the fields that are valid for that type.
 *
 * All field values remain strings (they come from form inputs), but the
 * discriminated union ensures compile-time safety for field access and updates.
 */

import type { ChildEntryType } from '../../types';

// ── Per-type field schemas ───────────────────────────────────────────────────
// Each interface lists only the fields valid for that entry type.
// Optional fields may be absent from the form state.

export interface TollFormData {
  type: 'toll';
  date?: string;
  amount?: string;
  toll_name?: string;
  payment_method?: string;
  notes?: string;
}

export interface FuelFormData {
  type: 'fuel';
  date?: string;
  amount?: string;
  litres?: string;
  fuel_type?: string;
  odometer?: string;
  station?: string;
  payment_method?: string;
}

export interface MaintenanceFormData {
  type: 'maintenance';
  date?: string;
  description?: string;
  maintenance_type?: string;
  amount?: string;
  odometer?: string;
  payment_method?: string;
}

export interface SiteVisitFormData {
  type: 'site_visit';
  site_name?: string;
  purpose?: string;
  contact_person?: string;
  notes?: string;
}

export interface AssessmentFormData {
  type: 'assessment';
  assessment_type?: string;
  result?: string;
  score?: string;
  notes?: string;
}

export interface SupervisionFormData {
  type: 'supervision';
  area?: string;
  team_size?: string;
  notes?: string;
}

export interface TrainingFormData {
  type: 'training';
  topic?: string;
  attendees?: string;
  duration_minutes?: string;
  notes?: string;
}

export interface RouteStopFormData {
  type: 'route_stop';
  stop_name?: string;
  purpose?: string;
  duration_minutes?: string;
  notes?: string;
}

export interface PurchaseFormData {
  type: 'purchase';
  date?: string;
  description?: string;
  amount?: string;
  vendor?: string;
  receipt_number?: string;
  category?: string;
  payment_method?: string;
}

export interface DeliveryNoteFormData {
  type: 'delivery_note';
  date?: string;
  description?: string;
  delivery_number?: string;
  notes?: string;
}

export interface SupplierInvoiceFormData {
  type: 'supplier_invoice';
  date?: string;
  description?: string;
  amount?: string;
  invoice_number?: string;
  due_date?: string;
  notes?: string;
}

export interface SupplierQuoteFormData {
  type: 'supplier_quote';
  date?: string;
  description?: string;
  quote_number?: string;
  valid_until?: string;
  amount?: string;
  notes?: string;
}

export interface GrnFormData {
  type: 'grn';
  date?: string;
  description?: string;
  grn_number?: string;
  received_by?: string;
  notes?: string;
}

export interface PurchaseReturnFormData {
  type: 'purchase_return';
  date?: string;
  description?: string;
  return_number?: string;
  return_reason?: string;
  amount?: string;
  notes?: string;
}

export interface CasualWageFormData {
  type: 'casual_wage';
  date?: string;
  description?: string;
  amount?: string;
  payment_method?: string;
  reimbursement_route?: string;
  notes?: string;
}

export interface TravelFormData {
  type: 'travel';
  date?: string;
  description?: string;
  distance_km?: string;
  rate_per_km?: string;
  amount?: string;
  vehicle_registration?: string;
  trip_ids?: string;
  trip_count?: string;
  payment_method?: string;
  reimbursement_route?: string;
  notes?: string;
}

// ── Discriminated union ──────────────────────────────────────────────────────

export interface SupplierCreditFormData {
  type: 'supplier_credit';
  date?: string;
  description?: string;
  amount?: string;
  credit_note_number?: string;
  notes?: string;
}

export type CaptureFormData =
  | TollFormData
  | FuelFormData
  | MaintenanceFormData
  | SiteVisitFormData
  | AssessmentFormData
  | SupervisionFormData
  | TrainingFormData
  | RouteStopFormData
  | PurchaseFormData
  | DeliveryNoteFormData
  | SupplierInvoiceFormData
  | SupplierQuoteFormData
  | GrnFormData
  | PurchaseReturnFormData
  | SupplierCreditFormData
  | CasualWageFormData
  | TravelFormData;

// ── Type helpers ─────────────────────────────────────────────────────────────

/** Get the schema type for a specific ChildEntryType */
export type SchemaForType<T extends ChildEntryType> = Extract<CaptureFormData, { type: T }>;

/** Get all valid field keys for a specific entry type */
export type FieldKeysForType<T extends ChildEntryType> = keyof Omit<SchemaForType<T>, 'type'>;

/** Get all valid field keys across all entry types (for generic access) */
export type CaptureFieldKey = keyof Omit<CaptureFormData, 'type'>;

// ── Factory: create initial form data for a given entry type ──────────────────

const DEFAULT_PAYMENT_METHODS: Partial<Record<ChildEntryType, string>> = {
  fuel: 'Company Card',
  toll: 'Company Card',
  maintenance: 'Company Card',
  purchase: 'Company Card',
  casual_wage: 'Cash',
  travel: 'Employee Paid (Seeking Reimbursement)',
};

/**
 * Create the initial typed form data for a given entry type.
 * Returns the discriminated union member with default values populated.
 */
export function createInitialFormData<T extends ChildEntryType>(
  entryType: T,
): SchemaForType<T> {
  const defaultPaymentMethod = DEFAULT_PAYMENT_METHODS[entryType];
  const base = { type: entryType } as unknown as SchemaForType<T>;
  if (defaultPaymentMethod) {
    (base as unknown as Record<string, unknown>).payment_method = defaultPaymentMethod;
  }
  return base;
}

// ── Field access helpers ─────────────────────────────────────────────────────

/**
 * Type-safe field getter. Returns undefined if the field is not valid for the
 * current entry type or is not set.
 */
export function getField<T extends ChildEntryType>(
  data: CaptureFormData,
  key: CaptureFieldKey,
): string | undefined {
  if (data.type === undefined) return undefined;
  return (data as unknown as Record<string, string | undefined>)[key as string];
}

/**
 * Type-safe field setter. Returns a new CaptureFormData with the field updated.
 * Only sets the field if it's valid for the current entry type.
 */
export function setField(
  data: CaptureFormData,
  key: CaptureFieldKey,
  value: string,
): CaptureFormData {
  return { ...data, [key]: value } as CaptureFormData;
}

/**
 * Check if a field key is valid for a given entry type.
 */
const FIELDS_BY_TYPE: Record<ChildEntryType, ReadonlySet<string>> = {
  toll: new Set(['type', 'amount', 'toll_name', 'payment_method', 'notes']),
  fuel: new Set(['type', 'amount', 'litres', 'fuel_type', 'odometer', 'station', 'payment_method']),
  maintenance: new Set(['type', 'description', 'maintenance_type', 'amount', 'odometer', 'payment_method']),
  site_visit: new Set(['type', 'site_name', 'purpose', 'contact_person', 'notes']),
  assessment: new Set(['type', 'assessment_type', 'result', 'score', 'notes']),
  supervision: new Set(['type', 'area', 'team_size', 'notes']),
  training: new Set(['type', 'topic', 'attendees', 'duration_minutes', 'notes']),
  route_stop: new Set(['type', 'stop_name', 'purpose', 'duration_minutes', 'notes']),
  purchase: new Set(['type', 'description', 'amount', 'vendor', 'receipt_number', 'category', 'payment_method']),
  delivery_note: new Set(['type', 'description', 'delivery_number', 'notes']),
  supplier_invoice: new Set(['type', 'description', 'amount', 'invoice_number', 'due_date', 'notes']),
  supplier_quote: new Set(['type', 'description', 'quote_number', 'valid_until', 'amount', 'notes']),
  grn: new Set(['type', 'description', 'grn_number', 'received_by', 'notes']),
  purchase_return: new Set(['type', 'description', 'return_number', 'return_reason', 'amount', 'notes']),
  supplier_credit: new Set(['type', 'description', 'amount', 'credit_note_number', 'notes']),
  casual_wage: new Set(['type', 'description', 'amount', 'payment_method', 'reimbursement_route', 'notes']),
  travel: new Set(['type', 'description', 'distance_km', 'rate_per_km', 'amount', 'vehicle_registration', 'trip_ids', 'trip_count', 'payment_method', 'reimbursement_route', 'notes']),
};

export function isValidFieldForType(
  entryType: ChildEntryType,
  key: string,
): boolean {
  return FIELDS_BY_TYPE[entryType]?.has(key) ?? false;
}

/**
 * Get the entry type from form data.
 */
export function getEntryType(data: CaptureFormData): ChildEntryType {
  return data.type;
}

/**
 * Convert typed form data to the legacy Record<string, string> format.
 * This is a bridge for components that haven't been updated yet.
 */
export function toRecordString(data: CaptureFormData): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(data)) {
    if (key !== 'type' && value != null) {
      result[key] = String(value);
    }
  }
  return result;
}

/**
 * Convert a Record<string, string> back to typed form data for a given entry type.
 * Only fields valid for the entry type are preserved.
 */
export function fromRecordString(
  entryType: ChildEntryType,
  record: Record<string, string>,
): CaptureFormData {
  const typed = createInitialFormData(entryType);
  for (const [key, value] of Object.entries(record)) {
    if (isValidFieldForType(entryType, key)) {
      (typed as unknown as Record<string, unknown>)[key] = value;
    }
  }
  return typed;
}

// ── Capture capabilities (trust model) ──────────────────────────────────────

/**
 * Explicit capability flags that control what the user can do in the capture form.
 * Replaces the old `disableGlAllocation` boolean with a granular, declarative model.
 *
 * Callers should pass one of the preset constants (`FULL_TRUST_CAPABILITIES` or
 * `ZERO_TRUST_CAPABILITIES`) or a partially-overridden object.
 */
export interface CaptureCapabilities {
  /** Whether the user can select GL account codes, allocation targets, and split allocations for line items. */
  canAllocateGl: boolean;
  /** Whether the user can switch expense types from within the camera (quick-switch pills). */
  canSwitchEntryTypeInCamera: boolean;
  /** Whether AI GL suggestions are available for line items. */
  canUseAiGlSuggestions: boolean;
}

/** Full-trust context — finance users (SmartAutomation, SmartFinance). All capabilities enabled. */
export const FULL_TRUST_CAPABILITIES: CaptureCapabilities = {
  canAllocateGl: true,
  canSwitchEntryTypeInCamera: true,
  canUseAiGlSuggestions: true,
};

/** Zero-trust context — field workers (SmartTaskModalStack). All capabilities disabled. */
export const ZERO_TRUST_CAPABILITIES: CaptureCapabilities = {
  canAllocateGl: false,
  canSwitchEntryTypeInCamera: false,
  canUseAiGlSuggestions: false,
};

/**
 * Resolve effective capabilities by combining the base capabilities with
 * runtime context (e.g. job-linked expenses always disable GL allocation).
 */
export function resolveCapabilities(
  base: CaptureCapabilities,
  isJobLinkedExpense: boolean,
): CaptureCapabilities {
  return {
    canAllocateGl: base.canAllocateGl && !isJobLinkedExpense,
    canSwitchEntryTypeInCamera: base.canSwitchEntryTypeInCamera,
    canUseAiGlSuggestions: base.canUseAiGlSuggestions && !isJobLinkedExpense,
  };
}
